import { useEffect, useState, useCallback } from 'react';
import {
  ResponsiveContainer,
  PieChart, Pie, Cell, Tooltip as RTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  fetchConversionMetrics,
  fetchLeadsBySource,
  fetchLeadsByStage,
  fetchLeadsByRegion,
  fetchAgentPerformance,
} from '../api/reportService';

const TABS = [
  { key: 'conversion', label: 'Conversion Metrics' },
  { key: 'source',     label: 'Leads by Source' },
  { key: 'stage',      label: 'Leads by Stage' },
  { key: 'region',     label: 'Leads by Region' },
  { key: 'agent',      label: 'Agent Performance' },
];

const PALETTE = ['#6366f1', '#22c55e', '#f43f5e', '#f59e0b', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6'];

/* ── helpers ─────────────────────────────── */
function ChartShell({ title, children }) {
  return (
    <div className="report-chart-card">
      {title && <h4 className="report-chart-title">{title}</h4>}
      {children}
    </div>
  );
}

function EmptyState({ text }) {
  return <p className="report-empty">{text || 'No data available.'}</p>;
}

function LoadingState() {
  return <p className="report-loading">Loading…</p>;
}

function KpiStrip({ items }) {
  return (
    <div className="report-kpi-strip">
      {items.map((item) => (
        <div key={item.label} className="report-kpi-card" style={{ borderTopColor: item.color || '#6366f1' }}>
          <span className="report-kpi-label">{item.label}</span>
          <strong className="report-kpi-value" style={{ color: item.color || '#6366f1' }}>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

/* ── date filter ─────────────────────────── */
function DateFilter({ from, to, onFromChange, onToChange, onApply }) {
  return (
    <div className="filter-bar">
      <label className="filter-date-label">
        From
        <input type="date" className="filter-input" value={from} onChange={(e) => onFromChange(e.target.value)} />
      </label>
      <label className="filter-date-label">
        To
        <input type="date" className="filter-input" value={to} onChange={(e) => onToChange(e.target.value)} />
      </label>
      <button className="primary-btn" onClick={onApply}>Apply</button>
    </div>
  );
}

/* ── custom tooltip ──────────────────────── */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="report-tooltip">
      {label && <p className="report-tooltip-label">{label}</p>}
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color, margin: '2px 0' }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
}

/* ── Conversion tab ──────────────────────── */
function ConversionTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetchConversionMetrics(params)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  }, [params]);

  if (loading) return <LoadingState />;
  if (error)   return <p className="error-text">{error}</p>;
  if (!data)   return null;

  const convPct = data.conversion_rate ? (data.conversion_rate * 100).toFixed(1) : '0.0';

  const pieData = [
    { name: 'Converted', value: data.converted_leads || 0 },
    { name: 'Lost',      value: data.lost_leads      || 0 },
    { name: 'Rejected',  value: data.rejected_leads  || 0 },
    { name: 'Open', value: Math.max(0, (data.total_leads || 0) - (data.converted_leads || 0) - (data.lost_leads || 0) - (data.rejected_leads || 0)) },
  ].filter((d) => d.value > 0);

  const PIE_COLORS = ['#22c55e', '#f43f5e', '#f59e0b', '#6366f1'];

  return (
    <div>
      <KpiStrip items={[
        { label: 'Total Leads',     value: data.total_leads,     color: '#6366f1' },
        { label: 'Converted',       value: data.converted_leads, color: '#22c55e' },
        { label: 'Lost',            value: data.lost_leads,      color: '#f43f5e' },
        { label: 'Rejected',        value: data.rejected_leads,  color: '#f59e0b' },
        { label: 'Conversion Rate', value: `${convPct}%`,        color: '#06b6d4' },
      ]} />

      <div className="report-charts-row">
        <ChartShell title="Lead Outcome Breakdown">
          {pieData.length === 0 ? <EmptyState /> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={70} outerRadius={110}
                  paddingAngle={3} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <RTooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartShell>

        <ChartShell title="Conversion Rate">
          <div className="report-conv-gauge">
            <svg viewBox="0 0 200 110" className="report-gauge-svg">
              <path d="M 20 100 A 80 80 0 0 1 180 100" stroke="#e5e7eb" strokeWidth="18" fill="none" strokeLinecap="round" />
              <path d="M 20 100 A 80 80 0 0 1 180 100"
                stroke="#6366f1" strokeWidth="18" fill="none" strokeLinecap="round"
                strokeDasharray={`${(parseFloat(convPct) / 100) * 251.2} 251.2`} />
              <text x="100" y="88" textAnchor="middle" fontSize="28" fontWeight="700" fill="#6366f1">{convPct}%</text>
              <text x="100" y="106" textAnchor="middle" fontSize="12" fill="#6b7280">Conversion Rate</text>
            </svg>
          </div>
        </ChartShell>
      </div>
    </div>
  );
}

/* ── Source tab ──────────────────────────── */
function SourceTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetchLeadsBySource(params)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  }, [params]);

  if (loading) return <LoadingState />;
  if (error)   return <p className="error-text">{error}</p>;

  const rows = data?.rows || [];
  if (rows.length === 0) return <EmptyState text="No source data available." />;

  const total = rows.reduce((s, r) => s + (r.count || 0), 0);

  return (
    <div>
      <KpiStrip items={[
        { label: 'Total Sources', value: rows.length,       color: '#6366f1' },
        { label: 'Total Leads',   value: total,             color: '#06b6d4' },
        { label: 'Top Source',    value: rows[0]?.source || '—', color: '#22c55e' },
      ]} />
      <div className="report-charts-row">
        <ChartShell title="Leads by Source">
          <ResponsiveContainer width="100%" height={Math.max(260, rows.length * 44)}>
            <BarChart data={rows} layout="vertical" margin={{ left: 20, right: 30, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="source" width={120} tick={{ fontSize: 12 }} />
              <RTooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Leads" radius={[0, 4, 4, 0]}>
                {rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartShell>

        <ChartShell title="Source Share">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={rows.map((r) => ({ name: r.source, value: r.count }))}
                cx="50%" cy="50%" outerRadius={100} dataKey="value"
                label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <RTooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartShell>
      </div>
    </div>
  );
}

/* ── Stage tab ───────────────────────────── */
function StageTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetchLeadsByStage(params)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  }, [params]);

  if (loading) return <LoadingState />;
  if (error)   return <p className="error-text">{error}</p>;

  const rows = data?.rows || [];
  if (rows.length === 0) return <EmptyState text="No stage data available." />;

  const total = rows.reduce((s, r) => s + (r.count || 0), 0);

  const STAGE_COLORS = {
    new: '#6366f1', contacted: '#06b6d4', qualified: '#8b5cf6',
    proposal: '#f59e0b', negotiation: '#ec4899',
    converted: '#22c55e', lost: '#f43f5e', rejected: '#9ca3af',
  };

  const colored = rows.map((r) => ({ ...r, fill: STAGE_COLORS[r.stage] || PALETTE[0] }));

  return (
    <div>
      <KpiStrip items={[
        { label: 'Total Stages',  value: rows.length,        color: '#6366f1' },
        { label: 'Total Leads',   value: total,              color: '#06b6d4' },
        { label: 'Largest Stage', value: rows[0]?.stage || '—', color: '#8b5cf6' },
      ]} />
      <ChartShell title="Leads by Stage (Funnel View)">
        <ResponsiveContainer width="100%" height={Math.max(280, rows.length * 50)}>
          <BarChart data={colored} layout="vertical" margin={{ left: 20, right: 40, top: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="stage" width={110} tick={{ fontSize: 12 }} />
            <RTooltip content={<CustomTooltip />} />
            <Bar dataKey="count" name="Leads" radius={[0, 6, 6, 0]}>
              {colored.map((r, i) => <Cell key={i} fill={r.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>
    </div>
  );
}

/* ── Region tab ──────────────────────────── */
function RegionTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetchLeadsByRegion(params)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  }, [params]);

  if (loading) return <LoadingState />;
  if (error)   return <p className="error-text">{error}</p>;

  const rows = data?.rows || [];
  if (rows.length === 0) return <EmptyState text="No region data available." />;

  const byCountry = Object.values(
    rows.reduce((acc, r) => {
      const key = r.country || 'Unknown';
      acc[key] = acc[key] || { name: key, value: 0 };
      acc[key].value += r.count || 0;
      return acc;
    }, {})
  );

  const barData = rows.map((r) => ({ label: `${r.country || '?'} / ${r.state || '?'}`, count: r.count }));
  const grandTotal = rows.reduce((s, r) => s + (r.count || 0), 0);

  return (
    <div>
      <KpiStrip items={[
        { label: 'Countries',   value: byCountry.length, color: '#6366f1' },
        { label: 'Regions',     value: rows.length,      color: '#06b6d4' },
        { label: 'Total Leads', value: grandTotal,       color: '#22c55e' },
      ]} />
      <div className="report-charts-row">
        <ChartShell title="Leads by Country / State">
          <ResponsiveContainer width="100%" height={Math.max(260, barData.length * 44)}>
            <BarChart data={barData} layout="vertical" margin={{ left: 20, right: 30, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11 }} />
              <RTooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Leads" radius={[0, 4, 4, 0]}>
                {barData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartShell>

        <ChartShell title="By Country">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={byCountry} cx="50%" cy="50%" outerRadius={100} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {byCountry.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <RTooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartShell>
      </div>
    </div>
  );
}

/* ── Agent Performance tab ───────────────── */
function AgentPerfTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetchAgentPerformance(params)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  }, [params]);

  if (loading) return <LoadingState />;
  if (error)   return <p className="error-text">{error}</p>;

  const rows = data?.rows || [];
  if (rows.length === 0) return <EmptyState text="No agent performance data available." />;

  const barData = rows.map((r) => ({
    name:          `Agent ${r.agent_id}`,
    Total:         r.total_leads        || 0,
    Converted:     r.converted_leads    || 0,
    'In Progress': r.in_progress_leads  || 0,
  }));

  const totals = rows.reduce(
    (acc, r) => ({
      total:     acc.total     + (r.total_leads       || 0),
      converted: acc.converted + (r.converted_leads   || 0),
      inProg:    acc.inProg    + (r.in_progress_leads  || 0),
    }),
    { total: 0, converted: 0, inProg: 0 }
  );

  const avgConv = totals.total > 0
    ? ((totals.converted / totals.total) * 100).toFixed(1)
    : '0.0';

  return (
    <div>
      <KpiStrip items={[
        { label: 'Agents',         value: rows.length,      color: '#6366f1' },
        { label: 'Total Leads',    value: totals.total,     color: '#06b6d4' },
        { label: 'Converted',      value: totals.converted, color: '#22c55e' },
        { label: 'In Progress',    value: totals.inProg,    color: '#f59e0b' },
        { label: 'Avg Conv. Rate', value: `${avgConv}%`,    color: '#8b5cf6' },
      ]} />
      <ChartShell title="Agent Performance Breakdown">
        <ResponsiveContainer width="100%" height={Math.max(300, barData.length * 60)}>
          <BarChart data={barData} margin={{ left: 10, right: 20, top: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <RTooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="Total"       fill="#6366f1" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Converted"   fill="#22c55e" radius={[4, 4, 0, 0]} />
            <Bar dataKey="In Progress" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>
    </div>
  );
}

const TAB_COMPONENTS = {
  conversion: ConversionTab,
  source:     SourceTab,
  stage:      StageTab,
  region:     RegionTab,
  agent:      AgentPerfTab,
};

/* ── Page ────────────────────────────────── */
function ReportsPage() {
  const [activeTab, setActiveTab] = useState('conversion');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [appliedParams, setAppliedParams] = useState({});

  const applyFilters = useCallback(() => {
    const p = {};
    if (from) p.from = new Date(from).toISOString();
    if (to)   p.to   = new Date(to + 'T23:59:59').toISOString();
    setAppliedParams(p);
  }, [from, to]);

  const TabContent = TAB_COMPONENTS[activeTab];

  return (
    <div>
      <div className="page-title-row">
        <h2>Reports</h2>
      </div>

      <DateFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} onApply={applyFilters} />

      <div className="tab-bar">
        {TABS.map((tab) => (
          <button key={tab.key}
            className={`tab-btn${activeTab === tab.key ? ' tab-btn--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        <TabContent params={appliedParams} />
      </div>
    </div>
  );
}

export default ReportsPage;
